import React, { useEffect, useState } from "react";
import { supabase } from "../supabase";

type MarkType = "main" | "duplicate" | "separate" | null;

interface Product {
  id: number;
  name: string;
  name_en: string;
  unit: string;
  category_id: number;
  synonyms: string[];
}

interface Group {
  id: string;
  products: Product[];
  marks: Record<number, MarkType>;
  applied: boolean;
}

function normalize(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-zа-яё0-9\s]/gi, "")
    .replace(/\s+/g, " ");
}

function similarity(a: string, b: string): number {
  const na = normalize(a);
  const nb = normalize(b);
  if (na === nb) return 1;
  if (na.includes(nb) || nb.includes(na)) return 0.9;

  // Общие слова
  const wa = na.split(" ");
  const wb = nb.split(" ");
  const common = wa.filter((w) =>
    wb.some((w2) => w2.startsWith(w) || w.startsWith(w2)),
  );
  if (common.length > 0) return 0.7;

  // Первые 4 символа совпадают
  if (na.substring(0, 4) === nb.substring(0, 4) && na.length > 3) return 0.6;

  return 0;
}

export default function Duplicates() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState<string | null>(null);
  const [threshold, setThreshold] = useState(0.7);
  const [appliedCount, setAppliedCount] = useState(0);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    findDuplicates();
  }, []);

  const findDuplicates = async () => {
    setLoading(true);
    const { data: products } = await supabase
      .from("products")
      .select("id, name, name_en, unit, category_id, synonyms")
      .order("name");

    if (!products) {
      setLoading(false);
      return;
    }

    const used = new Set<number>();
    const result: Group[] = [];

    for (let i = 0; i < products.length; i++) {
      if (used.has(products[i].id)) continue;
      const group: Product[] = [products[i]];

      for (let j = i + 1; j < products.length; j++) {
        if (used.has(products[j].id)) continue;
        const simRU = similarity(products[i].name, products[j].name);
        const simEN =
          products[i].name_en && products[j].name_en
            ? similarity(products[i].name_en, products[j].name_en)
            : 0;
        if (simRU >= threshold || simEN >= threshold) {
          group.push(products[j]);
          used.add(products[j].id);
        }
      }

      if (group.length > 1) {
        used.add(products[i].id);
        const marks: Record<number, MarkType> = {};
        group.forEach((p) => {
          marks[p.id] = null;
        });
        result.push({
          id: `group-${i}`,
          products: group,
          marks,
          applied: false,
        });
      }
    }

    setGroups(result);
    setLoading(false);
  };

  const setMark = (groupId: string, productId: number, mark: MarkType) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== groupId) return g;
        const newMarks = { ...g.marks };

        // Только один может быть "main"
        if (mark === "main") {
          Object.keys(newMarks).forEach((k) => {
            if (newMarks[Number(k)] === "main") newMarks[Number(k)] = null;
          });
        }
        newMarks[productId] = newMarks[productId] === mark ? null : mark;
        return { ...g, marks: newMarks };
      }),
    );
  };

  const applyGroup = async (group: Group) => {
    const mainId = Object.entries(group.marks).find(
      ([, v]) => v === "main",
    )?.[0];
    const duplicateIds = Object.entries(group.marks)
      .filter(([, v]) => v === "duplicate")
      .map(([k]) => Number(k));

    if (!mainId) {
      alert("Выберите основной продукт (🟢)");
      return;
    }
    if (duplicateIds.length === 0) {
      alert("Отметьте хотя бы один дубль (🔴)");
      return;
    }

    setApplying(group.id);

    for (const dupId of duplicateIds) {
      // Переносим ссылки
      await supabase
        .from("recipe_ingredients")
        .update({ product_id: Number(mainId) })
        .eq("product_id", dupId);
      await supabase
        .from("user_products")
        .update({ product_id: Number(mainId) })
        .eq("product_id", dupId);
      await supabase
        .from("shopping_list")
        .update({ product_id: Number(mainId) })
        .eq("product_id", dupId);
      await supabase
        .from("substitutions")
        .update({ substitute_product_id: Number(mainId) })
        .eq("substitute_product_id", dupId);

      // Добавляем в synonyms основного
      const dupProduct = group.products.find((p) => p.id === dupId);
      if (dupProduct) {
        const { data: mainProduct } = await supabase
          .from("products")
          .select("synonyms")
          .eq("id", Number(mainId))
          .single();

        const existingSynonyms: string[] = mainProduct?.synonyms || [];
        const newSynonyms = [
          ...new Set([
            ...existingSynonyms,
            dupProduct.name.toLowerCase(),
            ...(dupProduct.name_en ? [dupProduct.name_en.toLowerCase()] : []),
            ...(dupProduct.synonyms || []),
          ]),
        ];

        await supabase
          .from("products")
          .update({ synonyms: newSynonyms })
          .eq("id", Number(mainId));
      }

      // Удаляем дубль
      await supabase.from("products").delete().eq("id", dupId);
    }

    setApplying(null);
    setAppliedCount((c) => c + 1);
    setGroups((prev) =>
      prev.map((g) => (g.id === group.id ? { ...g, applied: true } : g)),
    );
  };

  const pendingGroups = groups.filter((g) => !g.applied);
  const doneGroups = groups.filter((g) => g.applied);

  const getMarkStyle = (
    mark: MarkType,
    current: MarkType,
  ): React.CSSProperties => {
    const isActive = mark === current;
    const base: React.CSSProperties = {
      padding: "4px 10px",
      borderRadius: 6,
      border: "2px solid",
      cursor: "pointer",
      fontSize: 13,
      fontWeight: 600,
      transition: "all 0.15s",
    };
    if (mark === "main")
      return {
        ...base,
        borderColor: "#2ECC71",
        backgroundColor: isActive ? "#2ECC71" : "#fff",
        color: isActive ? "#fff" : "#2ECC71",
      };
    if (mark === "duplicate")
      return {
        ...base,
        borderColor: "#e74c3c",
        backgroundColor: isActive ? "#e74c3c" : "#fff",
        color: isActive ? "#fff" : "#e74c3c",
      };
    return {
      ...base,
      borderColor: "#9b59b6",
      backgroundColor: isActive ? "#9b59b6" : "#fff",
      color: isActive ? "#fff" : "#9b59b6",
    };
  };

  return (
    <div>
      <div style={s.header}>
        <h2 style={s.title}>🔍 Дубликаты продуктов</h2>
        <div style={s.controls}>
          <label style={s.label}>
            Порог похожести:
            <select
              style={s.select}
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
            >
              <option value={1}>Только точные (100%)</option>
              <option value={0.9}>Очень похожие (90%)</option>
              <option value={0.7}>Похожие (70%)</option>
              <option value={0.6}>Все варианты (60%)</option>
            </select>
          </label>
          <button style={s.refreshBtn} onClick={findDuplicates}>
            🔄 Найти дубли
          </button>
        </div>
      </div>

      {appliedCount > 0 && (
        <div style={s.successBanner}>✅ Обработано групп: {appliedCount}</div>
      )}

      {loading ? (
        <div style={s.empty}>Анализируем базу...</div>
      ) : pendingGroups.length === 0 && doneGroups.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <div>Дубликатов не найдено!</div>
        </div>
      ) : (
        <>
          <div style={s.infoBanner}>
            Найдено <strong>{pendingGroups.length}</strong> групп. Для каждой:
            🟢 Основной — 🔴 Дубль (удалить + synonyms) — 🟣 Отдельный
            (оставить)
          </div>

          {pendingGroups.map((group) => {
            const mainId = Object.entries(group.marks).find(
              ([, v]) => v === "main",
            )?.[0];
            const dupCount = Object.values(group.marks).filter(
              (v) => v === "duplicate",
            ).length;
            const canApply = !!mainId && dupCount > 0;

            return (
              <div key={group.id} style={s.groupCard}>
                <div style={s.groupHeader}>
                  <span style={s.groupTitle}>
                    {group.products.map((p) => p.name).join(" / ")}
                    <span style={s.groupCount}>
                      {group.products.length} продукта
                    </span>
                  </span>
                </div>

                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>ID</th>
                      <th style={s.th}>Название RU</th>
                      <th style={s.th}>Название EN</th>
                      <th style={s.th}>Ед.</th>
                      <th style={s.th}>Синонимов</th>
                      <th style={s.th}>Отметка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.products.map((p) => {
                      const mark = group.marks[p.id];
                      const rowBg =
                        mark === "main"
                          ? "#f0faf4"
                          : mark === "duplicate"
                            ? "#fdf2f2"
                            : mark === "separate"
                              ? "#f8f4fd"
                              : "#fff";
                      return (
                        <tr
                          key={p.id}
                          style={{ ...s.tr, backgroundColor: rowBg }}
                        >
                          <td style={s.td}>
                            <span style={s.idBadge}>{p.id}</span>
                          </td>
                          <td style={s.td}>
                            <strong>{p.name}</strong>
                          </td>
                          <td style={s.td}>{p.name_en || "—"}</td>
                          <td style={s.td}>{p.unit}</td>
                          <td style={s.td}>{p.synonyms?.length || 0}</td>
                          <td style={s.td}>
                            <div style={s.markBtns}>
                              <button
                                style={getMarkStyle("main", mark)}
                                onClick={() => setMark(group.id, p.id, "main")}
                                title="Основной — остаётся"
                              >
                                🟢
                              </button>
                              <button
                                style={getMarkStyle("duplicate", mark)}
                                onClick={() =>
                                  setMark(group.id, p.id, "duplicate")
                                }
                                title="Дубль — удалить, добавить в synonyms"
                              >
                                🔴
                              </button>
                              <button
                                style={getMarkStyle("separate", mark)}
                                onClick={() =>
                                  setMark(group.id, p.id, "separate")
                                }
                                title="Отдельный — оставить как есть"
                              >
                                🟣
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div style={s.groupFooter}>
                  {canApply ? (
                    <div style={s.applyInfo}>
                      Основной:{" "}
                      <strong>
                        {
                          group.products.find((p) => p.id === Number(mainId))
                            ?.name
                        }
                      </strong>
                      {" · "}Удалить дублей: <strong>{dupCount}</strong>
                    </div>
                  ) : (
                    <div style={s.applyHint}>
                      {!mainId
                        ? "⬆️ Выберите основной (🟢)"
                        : "⬆️ Отметьте дубли (🔴)"}
                    </div>
                  )}
                  <div style={s.groupFooter}>
                    {canApply ? (
                      <div style={s.applyInfo}>
                        Основной:{" "}
                        <strong>
                          {
                            group.products.find((p) => p.id === Number(mainId))
                              ?.name
                          }
                        </strong>
                        {" · "}Удалить дублей: <strong>{dupCount}</strong>
                      </div>
                    ) : (
                      <div style={s.applyHint}>
                        {!mainId
                          ? "⬆️ Выберите основной (🟢)"
                          : "⬆️ Отметьте дубли (🔴)"}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        style={s.skipBtn}
                        onClick={() =>
                          setGroups((prev) =>
                            prev.map((g) =>
                              g.id === group.id ? { ...g, applied: true } : g,
                            ),
                          )
                        }
                      >
                        ⏭️ Пропустить
                      </button>
                      <button
                        style={{
                          ...s.applyBtn,
                          opacity: canApply ? 1 : 0.4,
                          cursor: canApply ? "pointer" : "not-allowed",
                        }}
                        onClick={() => canApply && applyGroup(group)}
                        disabled={!canApply || applying === group.id}
                      >
                        {applying === group.id
                          ? "Применяем..."
                          : "✅ Применить"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {doneGroups.length > 0 && (
            <div style={s.doneSection}>
              <div style={s.doneSectionTitle}>
                ✅ Обработанные группы ({doneGroups.length})
              </div>
              {doneGroups.map((g) => (
                <div key={g.id} style={s.doneCard}>
                  {g.products.map((p) => p.name).join(" / ")}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 12,
  },
  title: { fontSize: 24, fontWeight: 700, color: "#222", margin: 0 },
  controls: { display: "flex", gap: 12, alignItems: "center" },
  label: {
    fontSize: 13,
    color: "#555",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  select: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #ddd",
    fontSize: 13,
  },
  refreshBtn: {
    padding: "10px 20px",
    backgroundColor: "#f5f5f5",
    border: "1px solid #eee",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
  },
  successBanner: {
    backgroundColor: "#d5f5e3",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 16,
    fontSize: 14,
    color: "#2ECC71",
    fontWeight: 600,
  },
  infoBanner: {
    backgroundColor: "#fff3cd",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 16,
    fontSize: 14,
    color: "#856404",
  },
  empty: {
    textAlign: "center",
    color: "#aaa",
    padding: 60,
    backgroundColor: "#fff",
    borderRadius: 12,
    fontSize: 16,
  },
  groupCard: {
    backgroundColor: "#fff",
    borderRadius: 12,
    marginBottom: 16,
    overflow: "hidden",
    border: "1px solid #eee",
  },
  groupHeader: {
    padding: "12px 16px",
    backgroundColor: "#f9f9f9",
    borderBottom: "1px solid #eee",
  },
  groupTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#333",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  groupCount: {
    fontSize: 12,
    color: "#888",
    fontWeight: 400,
    backgroundColor: "#eee",
    padding: "2px 8px",
    borderRadius: 20,
  },
  table: { width: "100%", borderCollapse: "collapse" as const },
  th: {
    padding: "10px 16px",
    textAlign: "left" as const,
    fontSize: 12,
    color: "#888",
    fontWeight: 600,
    backgroundColor: "#fafafa",
    borderBottom: "1px solid #eee",
  },
  tr: { borderBottom: "1px solid #f5f5f5", transition: "background 0.1s" },
  td: {
    padding: "10px 16px",
    fontSize: 13,
    color: "#222",
    verticalAlign: "middle" as const,
  },
  idBadge: { fontSize: 11, color: "#aaa", fontFamily: "monospace" },
  markBtns: { display: "flex", gap: 6 },
  groupFooter: {
    padding: "12px 16px",
    borderTop: "1px solid #eee",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#fafafa",
  },
  applyInfo: { fontSize: 13, color: "#555" },
  applyHint: { fontSize: 13, color: "#aaa" },
  applyBtn: {
    padding: "8px 20px",
    backgroundColor: "#2ECC71",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 14,
  },
  doneSection: { marginTop: 24 },
  doneSectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#2ECC71",
    marginBottom: 8,
  },
  doneCard: {
    backgroundColor: "#f0faf4",
    borderRadius: 8,
    padding: "10px 16px",
    marginBottom: 6,
    fontSize: 13,
    color: "#555",
  },
  skipBtn: {
    padding: "8px 16px",
    backgroundColor: "#f5f5f5",
    border: "1px solid #ddd",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    color: "#555",
  },
};
